







import {Vue, Component, Prop, Watch} from 'vue-property-decorator'
import Bell from '../../assets/svg/bell.svg';
import NotificationList from './components/NotificationsList.vue';
import {NotificationDataHelper} from "@share/notifications/helpers/NotificationDataHelper";
import {EventBus} from "@common/EventBus";
import {Popup} from '@api/models/Popup';
import {parseDeepLink} from "@common/deepLink/deepLinkHelper";
import AnalyticsMixin from "@mixins/AnalyticsMixin";
import NotificationIcon from '../../assets/svg/notification-icon.svg';
import { SignalRMethod, RealtimeCallback } from "@api/repositories/reatime-repository/types";
import Debug from "debug";

const debug = Debug("notifications");

@Component({
    name: 'Notifications',
    components: { Bell, NotificationList, NotificationIcon }
})
export default class Notifications extends AnalyticsMixin {
    @Prop({required: true}) dataHelper: NotificationDataHelper;

    timerId?: number = undefined;
    generatedPopups: Record<string, string> = {};
    previousUnreadCount = 0;
    useRealtimeUpdates = true;
    notificationsUpdatedCallback: RealtimeCallback | null = null;

    get unreadCount(): number {
        return this.dataHelper?.unreadCount || 0;
    }

    get lang() {
        return this.$store.state.session?.language ?? 'en';
    }

    isMeetingCancelled(title: string) {
        return (title.startsWith('Встреча отменен')
            || title.startsWith('Meeting cancelled')
        );
    }

    generatePopups() {
        this.dataHelper.fetchUnread().then(data => {
            data.reverse().forEach(notification => {
                if (notification.type == 'campaign' && this.isMeetingCancelled(notification.title)) {
                    const deepLink = parseDeepLink(notification.deepLink);
                    if (deepLink.data && deepLink.data.id) {
                        EventBus.$emit('program/removeMeetingById', deepLink.data.id);
                    }
                }
            })
        });
    }

    async check() {
        await this.dataHelper.fetchUnreadCount();
        if (this.unreadCount > 0 && this.unreadCount != this.previousUnreadCount) {
            this.previousUnreadCount = this.unreadCount;
            this.generatePopups();
        }
    }

    mounted() {
        this.check();

        if (!this.useRealtimeUpdates) {
            this.timerId = window.setInterval(() => {
                if (!document.hidden) {
                    this.check();
                }
            }, 5000)
        } else {
            this.notificationsUpdatedCallback = (data: any) => {
                try {
                    const update = JSON.parse(data);
                    debug(`updated notifications from hub: %O`, update);
                    this.onReceiveNotificationsUpdates(update);
                }
                catch (e) {
                    this.$store.dispatch('reportError', e);
                }
            }
            this.connectToHub();
        }
    }

    beforeDestroy() {
        EventBus.$off('notification-opened');
        if (!this.useRealtimeUpdates) {
            clearInterval(this.timerId);
        } else {
            this.disconnectFromHub();
        }
    }

    private async connectToHub(lang = this.lang) {
        try {
            await this.dataHelper.realtimeRepository.attach({
                [`notifications.${lang}`]: {
                    [SignalRMethod.notificationsUpdated]: this.notificationsUpdatedCallback as RealtimeCallback
                }
            });
        }
        catch (e) {
            this.$store.dispatch('reportError', e);
        }
    }

    private async disconnectFromHub(lang = this.lang) {
        try {
            await this.dataHelper.realtimeRepository.detach({
                [`notifications.${lang}`]: {
                    [SignalRMethod.notificationsUpdated]: this.notificationsUpdatedCallback as RealtimeCallback
                }
            });
        }
        catch (e) {
            this.$store.dispatch('reportError', e);
        }
    }

    private onReceiveNotificationsUpdates(message: any) {
        if (message.error) {
            const popup = new Popup({
                text: this.$t(`common.${message.error}`).toString(),
                type: 'alert',
                showTimeInMilliseconds: 2000,
            });
        }
        if (message.unreadCount) {
            Vue.set(this.dataHelper, 'unreadCount', message.unreadCount);
        }
        if (this.unreadCount > 0 && this.unreadCount != this.previousUnreadCount) {
            this.previousUnreadCount = this.unreadCount;
            this.generatePopups();
        }
    }

    @Watch('lang')
    async onLanguageChanged(newLang: string, oldLang: string) {
        await this.connectToHub(newLang);
        await this.disconnectFromHub(oldLang);
    }
}
